import React, { useContext, useEffect, useRef, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { Field, Formik } from "formik";
import _, { camelCase, uniqueId } from 'lodash';

import { FitinsurAPI, MessageCenter } from 'utils';
import { GlobalAuthContext, GlobalLoaderContext } from 'store/context';

import ResponsiveTable from '../ResponsiveTable';
import Button, { ButtonContainer } from '../Button';
import ComboBox from '../Form/ComboBox3';
import styled, { css } from 'styled-components';
import Label from '../Form/Label';
import SearchField from '../Form/SearchField'
import { useGetQuery } from '../../services/QuestionnairesService';

import { TagInput, DragZoneContainer, DropZoneArea, TableContainer } from './styles';

const defaultExtensions = [
  '.doc', '.docm', '.docx', '.dot', '.dotm', '.dotx', '.htm',
  '.html', '.msg', '.pdf', '.rtf', '.txt', '.wpd', '.xhtml',
  '.xps', '.bmp', '.gif', '.heic', '.jpg', '.jpeg', '.png',
  '.tif', '.tiff', '.pot', '.potx', '.pps', '.ppt', '.pptm',
  '.pptx', '.csv', '.xls', '.xlsm', '.xlsx'
];

const DragZoneUpload = ({
  disabled,
  documentTypeRequired = true,
  documentType,
  fullHeight,
  autoUpload,
  idOperation,
  onSuccess,
  isDraft,
  isInternalDocument,
  options,
  s3upload,
  changeShowAttachments,
  showAttachmentsState,
  changeAttachments,
  question,
  periodicityText,
  periodicityOptions,
  description,
  labelDescription,
  format,
  endorsementEdit,
  match,
  hideColumns = false, 
  translateText = false,
  notShowColumns = false,
  showDragZoneText = false,
  dragZoneText = null,
  dsInsurer = null,
  show = true,
  partiesDocuments = false,
}) => {
  
  if(question.hiddenInput) return null;

  const mc = new MessageCenter();
  const fitinsur = new FitinsurAPI();

  const [searchList, setSearchList] = useState()

  const loading = useContext(GlobalLoaderContext);
  const authContext = useContext(GlobalAuthContext);

  const [isDragging, setIsDragging] = useState(false);
  const [pendingFiles, setPendingFiles] = useState([]);
  const [initialValue, setInitialValue] = useState(null);

  let filteredOptions = options

  if (question?.answer?.length && endorsementEdit) {
    let answer = JSON.parse(JSON.stringify(question.answer));

    if (typeof answer != 'string' && answer?.some((file) => file?.documentType?.idAnswerOption === '7524')) {
      options = options.filter((option) => option.value.idAnswerOption !== 7524)
    }
  } else {
    filteredOptions = options
  }

  const onDragOver = () => setIsDragging(true);

  const onDragEnter = () => setIsDragging(true);

  const onDragLeave = () => setIsDragging(false);

  const onDrop = () => setIsDragging(false);

  const { acceptedFiles, getRootProps, getInputProps } = useDropzone({ onDragOver, onDragEnter, onDragLeave, onDrop, disabled: !!disabled, accept: format || defaultExtensions });

  const {
    request: useGetQueryRequest,
  } = useGetQuery();

  useEffect(() => {
    if (autoUpload && acceptedFiles && acceptedFiles.length && !s3upload && question.answerOptions.length === 1) {
      uploadFiles(acceptedFiles, documentType, true, null, null, null, null, question.idPersonInsurer);
    } else if (autoUpload && acceptedFiles && acceptedFiles.length && !s3upload) { 
      uploadFiles(acceptedFiles, documentType, isInternalDocument)
    } else if (autoUpload && acceptedFiles && acceptedFiles.length && s3upload) {
      uploadS3Files(acceptedFiles, documentType, isInternalDocument)
    } else if (!autoUpload && acceptedFiles && acceptedFiles.length) addPendingFiles(acceptedFiles);
  }, [acceptedFiles]);

  const addPendingFiles = async (files) => {
    const fileObjects = files.map(file => ({ file, type: file.type, name: file.name, folder: "", id: uniqueId() }))
    setPendingFiles(fileObjects);
  }

  const getFileParams = (file) => {
    if (question.idQuestion === 1867) {
      return {
        relevantTo: file.question_1868,
        whichInsurer: file.question_1869
      }
    }

    if (question.idQuestion === 2143) {
      return {
        relevantTo: file.question_2144,
        whichInsurer: file.question_2145
      }
    }

    if (question.idQuestion === 1941) {
      return {
        relevantTo: file.question_3108,
        whichInsurer: file.question_3109
      }
    }
}

  const confirmPendingFiles = async () => {
    const uploadPromises = []
    for (const pendingFile of pendingFiles) {
      const fileParams = getFileParams(pendingFile);
      uploadPromises.push( await uploadFiles([pendingFile.file], pendingFile.documentType, pendingFile.isInternalDocument, pendingFile.folder, pendingFile.periodicity, fileParams?.relevantTo, fileParams?.whichInsurer, question.idPersonInsurer))
    }
    const responses = await Promise.all(uploadPromises);
    loading.stop();
    setPendingFiles([])
  }

  // Função auxiliar da upLoadFiles, para validação de inputs
  function validateInputs(documentType, files, relevantToQst, whichInsurer) {
    if (documentTypeRequired && !documentType) {
      throw new Error(endorsementEdit || translateText ? 'Specify the type of document.' : "Por favor, especifique o tipo de documento.");
    }

    if (!files || !files[0]) {
      throw new Error(endorsementContracts ? 'Unsupported file type' : "Tipo de arquivo não suportado");
    }

    if (question.idQuestion === 2143) { // Validação para documentos de partes
      if (partiesDocuments && !documentType) throw new Error("The 'Document Type' field is required.");
      if (partiesDocuments && (!relevantToQst || !relevantToQst.idAnswerOption)) throw new Error("The 'Party Type' field is required.");
      if (partiesDocuments && (!whichInsurer || !whichInsurer.id)) throw new Error("The 'Party Name' field is required.");
    }

    if (question === 1867) { // Validação para endorsement
      const endorsementContracts = pendingFiles.filter(file => file?.documentType?.idAnswerOption === 7524);
      if (endorsementEdit && endorsementContracts.length > 1) throw new Error('Add just one endorsement contract.');
      if (endorsementEdit && (!relevantToQst || !relevantToQst.idAnswerOption)) throw new Error("The 'Relevant to' field is required.");
      if (endorsementEdit && relevantToQst.idAnswerOption === 7528 && (!whichInsurer || !whichInsurer.id)) throw new Error("The 'Which Insurer' field is required.");
    }
  }

  // Função auxiliar da uploadFiles, para preencher FormData
  function populateFormData(files, documentType, periodicity, folder, relevantToQst, whichInsurer, idPersonInsurer) {
    const formData = new FormData();
    
    files.forEach(file => formData.append('files', file));
    formData.append('idOperation', idOperation || "");
    formData.append('idTypeDocument', documentType?.idAnswerOption || "");
    formData.append('dsTypeDocument', documentType?.dsAnswerOption || "");
    formData.append('periodicity', !periodicity || periodicity === '-' ? "" : periodicity);
    formData.append('folder', folder);
    formData.append('idRelevantTo', relevantToQst?.idAnswerOption || "");
    formData.append('dsRelevantTo', relevantToQst?.dsAnswerOption || "");
    formData.append('idWhichInsurer', whichInsurer?.id || "");
    formData.append('dsWhichInsurer', whichInsurer?.value || "");
    if (whichInsurer?.id) formData.append('idPersonInsurer', whichInsurer?.id || "");
    if (idPersonInsurer) formData.append('idPersonInsurer', idPersonInsurer || "");

    const optionBehaviorObject = question?.answerOptions?.find(
      opt => opt.idAnswerOption === documentType?.idAnswerOption
    )?.behaviorObject;

    if (optionBehaviorObject?.isResultDomainDocument) {
      formData.append('resultIdDomainDocument', optionBehaviorObject.resultIdDomainDocument);
    }
    
    if (optionBehaviorObject?.isResultDomainStatusDocument) {
      formData.append('resultIdDomainStatusDocument', optionBehaviorObject.resultIdDomainStatusDocument);
    }

    if (isDraft) formData.append('isDraft', "true");
    if (isInternalDocument) formData.append('isInternalDocument', "true");

    return formData;
  }

 const uploadFiles = async (
  files, 
  documentType, 
  isInternalDocument = false, 
  folder = "", 
  periodicity, 
  relevantToQst = null, 
  whichInsurer = null, 
  idPersonInsurer = null
) => {
  try {
    validateInputs(documentType, files, relevantToQst, whichInsurer);

    loading.start();
    
    const formData = populateFormData(files, documentType, periodicity, folder, relevantToQst, whichInsurer, idPersonInsurer);

    fitinsur.setAuthToken(authContext.token);
    const response = await fitinsur.requestFileUpload(formData).then(response => response.json());

    if (response.ret_code !== 200) throw new Error(response.ret_msg_details);

    if (!autoUpload && documentTypeRequired) return response.ret_value;

    loading.stop();
    onSuccess && onSuccess(response.ret_value);
  } catch (err) {
    console.log('UploadButton - error', err);
    mc.error(err.message);
    loading.stop();
  }
};

  const uploadS3Files = async (files, documentType, isInternalDocument = false, folder = "", periodicity, relevantToQst = null, whichInsurer = null) => {
    try {
      if (documentTypeRequired && !documentType) throw new Error("Por favor, especifique o tipo de documento.");
      if (!files || !files[0]) throw new Error("Tipo de arquivo não surportado");

      loading.start();
      const formData = new FormData();

      for (const file of files) formData.append('files', file)
      formData.append('idOperation', idOperation || "");
      formData.append('idTypeDocument', documentType?.idAnswerOption || "")
      formData.append('dsTypeDocument', documentType?.dsAnswerOption || "")
      formData.append('periodicity', !periodicity || periodicity === '-' ? "" : periodicity)
      formData.append('folder', folder)
      formData.append('idRelevantTo', relevantToQst?.idAnswerOption || "")
      formData.append('dsRelevantTo', relevantToQst?.dsAnswerOption || "")
      formData.append('idWhichInsurer', whichInsurer?.id || '')
      formData.append('dsWhichInsurer', whichInsurer?.value || '')
      if (whichInsurer?.id) {
        formData.append('idPersonInsurer', whichInsurer?.id || '')
      }

      let optionBehaviorObject = null;
      if (question && Array.isArray(question?.answerOptions) && question.answerOptions.length) {
        optionBehaviorObject = question.answerOptions.find(
          opt => opt.idAnswerOption === documentType?.idAnswerOption
        ).behaviorObject;
      }
      const { isResultDomainDocument, isResultDomainStatusDocument } = optionBehaviorObject || {};
      if (isResultDomainDocument) {
        formData.append('resultIdDomainDocument', optionBehaviorObject.resultIdDomainDocument)
      }

      if (isResultDomainStatusDocument) {
        formData.append('resultIdDomainStatusDocument', optionBehaviorObject.resultIdDomainStatusDocument)
      }

      if (isDraft) formData.append('isDraft', "true")
      if (isInternalDocument) formData.append('isInternalDocument', "true")

      fitinsur.setAuthToken(authContext.token);
      const response = await fitinsur.requestFileUploadS3(formData).then(response => response.json());
      if (response.ret_code !== 200) throw new Error(response.ret_msg_details)

      if (!autoUpload && documentTypeRequired) return response.ret_value;

      loading.stop();
      onSuccess && onSuccess(response.ret_value);
    } catch (err) {
      console.log('UploadButton - error', err);
      mc.error(err.message);
      loading.stop()
    }
  }

  const renderNameColumnName = () => {
    if (question?.idQuestion === 2143) return  "Document Name";
    return endorsementEdit || translateText ? "Name" : "Nome";
  }

  const renderTypeColumnName = () => {
    if (question?.idQuestion === 2143) return  "Document Type";
    return endorsementEdit || translateText ? "Type" : "Tipo";
  }


  const defaultColumns = [
    {
      title: renderNameColumnName(),
      key: "name",
      minWidth: 20,
      maxWidth: 20
    },
    {
      title: renderTypeColumnName(),
      key: "type",
      minWidth: 20,
      maxWidth: 20,
      component: (row) => <Field
        name={`documentType-${row.id}`}
        type="text"
        topReduce
        isRequired={false}
        component={ComboBox}
        options={options || []}
        validation={false}
        searchList={[]}
        onChange={(value) => {
          let originValue = question?.answerOptions?.find(ansOpt => ansOpt.idAnswerOption == value.idAnswerOption)

          setPendingFiles(pendingFiles.map(file => file.id !== row.id ? file : {
            ...file,
            documentType: value,
            isInternalDocument: value.behavior && (
              value.behavior.folder === "Internal Documents" ||
              value.behavior.folder === "Documentos Internos"
            ),
            folder: value.behavior?.folder || originValue?.behaviorObject?.folder,
            periodicity: periodicityOptions?.includes(value.idAnswerOption) ? periodicityText : '-'
          }))
        }}
      />
    },
    (periodicityOptions?.length ? {
      title: "Periodicidade",
      key: "periodicity",
      minWidth: 20,
      maxWidth: 20,
    } : null),
    {
      title: endorsementEdit || translateText ? "Folder" : "Pasta",
      key: "folder",
      minWidth: 20,
      maxWidth: 20,
    },
    {
      title: "",
      key: "",
      minWidth: 1,
      maxWidth: 1,
    }
  ];

  const setSearchQueryId = (value, idQuestion) => {
    const { idAnswerOption } = value
    const childrenQuestion = question.childrenQuestions.find((qst) => qst.idQuestion === idQuestion);
    let searchQueryId = null;
    switch (idAnswerOption) {
      case 7757:
        searchQueryId = 'getClient';
        break;
      case 7758:
        searchQueryId = 'getInsurers';
        break;
      case 7759:
        searchQueryId = 'getExternalBroker';
        break;
      case 7760:
        searchQueryId = 'getThirdParties';
        break;
    }

    return searchQueryId;
  }


  const getOptions = (question, file) => {

    if (![43, 44, 270, 271].includes(question.idDomainQuestion)) return [];

    if (question.answerOptions) {
      const { answerOptions } = question;

      const options = answerOptions.map((option) => ({
        label: option.dsAnswerOption,
        value: {
          idAnswerOption: option.idAnswerOption,
          dsAnswerOption: option.dsAnswerOption,
        }
      }))

      return options;
    } else {
      return [];
    }

  }
  const notNullColumns = defaultColumns.filter((column) => column !== null)
  let columns = question?.childrenQuestions ? notNullColumns.filter((column) => column.key === 'type' || column.key === 'name') : defaultColumns;

  // filtering columns to show just the name column
  columns = notShowColumns ? [columns[0]] : columns

  const searchRequest = async (searchValue, question, setSearchList, searchQueryIdRow) => {
    if (question) {
      let qstBehavior = question.behaviorObject;

      if (qstBehavior?.searchQueryId || searchQueryIdRow) {
        let searchQueryId = qstBehavior?.searchQueryId || searchQueryIdRow;
        const { data } = await useGetQueryRequest({
          id: searchQueryId,
          params: {
            id: searchQueryId,
            queryTail: true,
            personName: searchValue || '',
            idOperation
          }
        });
        
        try {
          if (data.ret_code !== 200) return;
          const format = qstBehavior.searchQueryId === 'fitQueryPersonClient' || qstBehavior.searchQueryId === 'fitGetPersonSurplusBroker' || qstBehavior.searchQueryId === 'fitGetPersonThirdParty';
          const persons = data.ret_value.map(person => {
            if (format) {
              return {
                id: person.id,
                value: person.personName
              }
            } else {
              return {
                ...person,
              }
            }
          })
          return setSearchList(persons);
        } catch (error) {
          console.log("err cnpj", error)
        }
      }

    }
  }

  if (question.childrenQuestions) {
    const { childrenQuestions } = question;
    for (const question of childrenQuestions) {
      const column = {
        title: question.behaviorObject.description,
        key: question.behaviorObject.description,
        minWidth: 20,
        maxWidth: 20,
        component: (row) => {
          const file = pendingFiles.find((file) => {
            return file.id === row.id
          })

          let childrenOptions = getOptions(question, file);

          const isEndorsementContract = file?.documentType?.idAnswerOption === 7524

          if (isEndorsementContract) {
            childrenOptions = childrenOptions.filter((option) => option.value.idAnswerOption === 7526)
            }
            
          const resetInputs = (isEndorsementContract && file.question_1868 && file.question_1868.idAnswerOption !== 7526);
            
          if(resetInputs) {
              pendingFiles.forEach((f) => {
                if(f === file){
                delete f.question_1868
                delete f.question_1869}
                })
                setPendingFiles([...pendingFiles])
                setInitialValue(' ')
          }

          if (question?.behaviorObject?.resetInputsBehaviorObject) {
            const resetDocumentTypeIdAnswerOption = question?.behaviorObject?.resetInputsBehaviorObject.documentTypeIdAnswerOption;
            const resetRelevantToIdQuestion = question?.behaviorObject?.resetInputsBehaviorObject.relevantToIdQuestion;
            const resetRelevantToIdAnswerOption = question?.behaviorObject?.resetInputsBehaviorObject.relevantToIdAnswerOption
            const resetRelevantToQuestionKey = `question_${resetRelevantToIdQuestion}`;

            const forceResetInputs = 
              file?.documentType?.idAnswerOption === resetDocumentTypeIdAnswerOption && 
              question.idQuestion === resetRelevantToIdQuestion && 
              file[resetRelevantToQuestionKey].idAnswerOption !== resetRelevantToIdAnswerOption;
            
            if (forceResetInputs) {
              pendingFiles.forEach((f) => {
                if (f === file) {
                  delete f[resetRelevantToQuestionKey]
                  delete f[resetRelevantToQuestionKey]
                }
              })
              setPendingFiles([...pendingFiles])
              setInitialValue(' ')
            }
          }

          let allInsurersOpt =options.find((opt) => opt.label === 'All Insurers')
          if (question.idDomainQuestion === 276 && question.idQuestion === 1869) {
            if(!file.documentType || isEndorsementContract) {
              return null
            }
            if (!file.documentType?.idAnswerOption === 7524 || !file.question_1868 || file.question_1868.idAnswerOption !== 7528) return null;
          }

          if (question.idDomainQuestion === 276 && question.idQuestion === 3109) {
            if (!file.question_3108 || file.question_3108.idAnswerOption !== 8654) return null;
          }

          return (
            <Field
              name={`question_${question.idQuestion}-${row.id}`}
              type="text"
              value={'teste'}
              question={question}
              topReduce
              isRequired={true}
              initialValue={initialValue}
              setInitialValue={setInitialValue}
              component={question.idDomainQuestion === 276 ? SearchField : ComboBox}
              options={childrenOptions || []}
              // disabled={isEndorsementContract}
              validation={false}
              searchRequest={(searchValue, question, setSearchList) => searchRequest(searchValue, question, setSearchList, row.searchQueryId ? row.searchQueryId : null)}
              onSearchSelect={(selectValue) => {
                if (selectValue) {
                  setPendingFiles(pendingFiles.map(file => file.id !== row.id ? file : {
                    ...file,
                    [`question_${question.idQuestion}`]: selectValue,
                  }))
                }
              }}
              searchList={question.idDomainQuestion === 276 ? searchList : []}
              onChange={(value) => {
                if (question.idDomainQuestion !== 276) {
                  setPendingFiles(pendingFiles.map(file => {
                    if (file.id !== row.id) {
                      return file
                    } else {
                      if ( question.idQuestion === 2144) file.searchQueryId = setSearchQueryId(value, 2145);
                      return {
                        ...file,
                        [`question_${question.idQuestion}`]: value,
                      }
                    }
                  }))
                }
              }}
            />
          )
        }
      }

      columns.push(column)
    }
  }

  if (changeAttachments) changeAttachments(pendingFiles);

  const changeRemoveAttachments = state => setPendingFiles(state)

  const renderDragZoneText = () => {
    if(showDragZoneText) return dragZoneText || description;

    if(endorsementEdit || translateText ) return "Drag and Drop Documents here to upload"

    return "Clique ou arraste e solte os documentos aqui para envio"
  }

  if(!show) return null
  return (
    <>
      {endorsementEdit || labelDescription && <Label>{labelDescription || description}</Label>}
      {!pendingFiles.length ?
        <DragZoneContainer isDragging={isDragging} fullHeight={fullHeight} disabled={disabled}>
          <DropZoneArea {...getRootProps({ className: 'dropzone', disabled })}>
            <p>{renderDragZoneText()}</p>
            <TagInput {...getInputProps()} />
          </DropZoneArea>
        </DragZoneContainer>
        :
        <TableContainer>          
          <ResponsiveTable 
            columns={columns?.filter(col => col)} 
            data={pendingFiles} 
            changeShowAttachments={changeShowAttachments} 
            changeRemoveAttachments={changeRemoveAttachments}
          />

          <ButtonContainer style={{ justifyContent: 'flex-end' }}>
            <Button
              onClick={() => {
                setPendingFiles([])
                changeShowAttachments && changeShowAttachments(!showAttachmentsState);
              }}
              third
            >
              {endorsementEdit || translateText ? 'CANCEL' : 'CANCELAR'}
            </Button>

            <Button
              onClick={(e) => confirmPendingFiles()}
              blue
            >
              {endorsementEdit || translateText ? 'CONFIRM' : 'CONFIRMAR'}
            </Button>
          </ButtonContainer>
        </TableContainer>
      }
    </>
  );
};


export default DragZoneUpload;
